import { createGqlClient } from './api.js';
import { ADMIN_BFF_URL } from './runtimeEnv.js';

/**
 * The CRM's own GraphQL schema, reached through the admin BFF's `POST /crm`
 * pass-through (docs/PARSINADENT_DEMO_REQUEST_DESIGN.md D6, §7).
 *
 * Built from `api.js`'s one transport core, so the token, the silent refresh
 * and the session-ended event are the platform client's, not a copy. Two
 * deliberate differences, both about what a failure MEANS here:
 *
 *   • FORBIDDEN does not take the panel away. On the platform schema it means
 *     "not staff" and AuthGate renders the whole-panel 403; here it means the
 *     CRM refused this caller — typically a CRM role granted as a realm role
 *     rather than a client role on `parsina-bff` (the claim-location trap) —
 *     and that is one screen's error state.
 *   • CRM_UNAVAILABLE, any 5xx and a body that is not JSON surface as the
 *     screen's error state with a retry, never as a session event.
 *
 * The schema contract is `marketing/parsina.bff.crm/src/graphql/typeDefs/*`.
 */
export const CRM_URL = `${String(ADMIN_BFF_URL).replace(/\/+$/, '')}/crm`;

export const crmGql = createGqlClient(CRM_URL, {
  dispatchForbidden: false,
  unavailableCode: 'CRM_UNAVAILABLE',
});

/**
 * The catalogue key for a CRM error, or null when the server's own message is
 * the better thing to show.
 *
 * `BAD_INPUT` and `NOT_ALLOWED` return null on purpose: the CRM passes the
 * marketing service's own wording through for those («lead cannot move from
 * 'won' to …», «due_at_ms is required …»), which is exactly what a rep needs
 * to understand a refused write.
 */
export function crmErrorKey(code) {
  switch (code) {
    case 'CRM_UNAVAILABLE':
    case 'SERVICE_UNAVAILABLE':
      return 'demoRequests.errors.unavailable';
    case 'FORBIDDEN':
      return 'demoRequests.errors.forbidden';
    case 'NOT_FOUND':
      return 'demoRequests.errors.notFound';
    default:
      return null;
  }
}

export function crmErrorMessage(err, t) {
  const key = crmErrorKey(err?.code);
  if (key) return t(key);
  return err?.message || t('errors.code.unknown');
}

/** Fired after any write on a lead, so the nav badge recounts without waiting a minute. */
export const DEMO_REQUESTS_CHANGED_EVENT = 'parsina:demo-requests-changed';

export function announceDemoRequestsChanged() {
  try {
    window.dispatchEvent(new Event(DEMO_REQUESTS_CHANGED_EVENT));
  } catch { /* no window (tests) */ }
}
